import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { useTheme } from "@emotion/react";
import React, { useState } from "react";
import FolderIcon from "@mui/icons-material/Folder";
import GridViewIcon from "@mui/icons-material/GridView";
import SendIcon from "@mui/icons-material/Send";
import { useProjectDocuments } from "../../hooks/api/projectDocumentsApiHooks";
import DocumentSlotsView from "./DocumentSlotsView";
import FileManager from "../fileManager/FileManager";
import BulkSendDocumentsModal from "./modals/BulkSendDocumentsModal";

/**
 * Componente principal para gerenciar documentos do projeto
 * Sistema híbrido: Slots específicos + FileManager tradicional
 * @param {object} props
 * @param {number} props.projectId - ID do projeto
 * @param {string} props.projectNumber - Número do projeto (para FileManager)
 * @param {object} props.projectData - Dados completos do projeto
 */
export default function ProjectDocumentsManager({ projectId, projectNumber, projectData }) {
  const theme = useTheme();

  // Estado de visualização: 'slots' ou 'filemanager'
  const [viewMode, setViewMode] = useState("slots");
  const [bulkSendModalOpen, setBulkSendModalOpen] = useState(false);

  // Buscar documentos do projeto
  const {
    data: documentsOverview,
    isLoading,
    error,
    refetch,
  } = useProjectDocuments(projectId, viewMode === "slots");

  // Toggle de visualização
  const handleToggleView = () => {
    setViewMode((prev) => {
      const newMode = prev === "slots" ? "filemanager" : "slots";
      // Quando voltar para slots, fazer refetch para sincronizar com FileManager
      if (newMode === "slots") {
        setTimeout(() => {
          refetch();
        }, 500);
      }
      return newMode;
    });
  };

  // Handler para envio em lote
  const handleBulkSendClick = () => {
    setBulkSendModalOpen(true);
  };

  const handleBulkSendSuccess = () => {
    setBulkSendModalOpen(false);
  };

  // Loading state
  if (isLoading && viewMode === "slots") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error && viewMode === "slots") {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Erro ao carregar documentos: {error.message || "Erro desconhecido"}
      </Alert>
    );
  }

  // Documentos disponíveis para envio em lote
  const availableDocuments = documentsOverview?.slots
    ?.filter((slot) => slot.hasDocument && slot.currentDocument)
    .map((slot) => slot.currentDocument) || [];

  return (
    <Box>
      {/* Header Compacto */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 2,
          gap: 1,
        }}
      >
        {/* Botão de Envio em Lote (apenas no modo slots) */}
        {viewMode === "slots" && availableDocuments.length > 0 && (
          <Button
            variant="outlined"
            color="success"
            startIcon={<SendIcon />}
            onClick={handleBulkSendClick}
            size="small"
          >
            Enviar em Lote
          </Button>
        )}

        {/* Toggle de visualização */}
        <Button
          variant={viewMode === "filemanager" ? "contained" : "outlined"}
          startIcon={viewMode === "filemanager" ? <GridViewIcon /> : <FolderIcon />}
          onClick={handleToggleView}
          size="small"
        >
          {viewMode === "filemanager" ? "Slots" : "Explorador"}
        </Button>
      </Box>

      {/* Conteúdo baseado no modo de visualização */}
      {viewMode === "slots" ? (
        <DocumentSlotsView
          projectId={projectId}
          project={projectData}
          slots={documentsOverview?.slots || []}
          statistics={documentsOverview?.statistics || null}
          onRefresh={refetch}
        />
      ) : (
        /* FileManager integrado sem accordion extra */
        <Box sx={{ 
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2,
          bgcolor: "background.paper",
        }}>
          <FileManager rootDirectory={`/${projectNumber}`} noAccordion={true} />
        </Box>
      )}

      {/* Modal de Envio em Lote */}
      {bulkSendModalOpen && (
        <BulkSendDocumentsModal
          isOpen={bulkSendModalOpen}
          onClose={() => setBulkSendModalOpen(false)}
          projectId={projectId}
          project={projectData}
          availableDocuments={availableDocuments}
          onSuccess={handleBulkSendSuccess}
        />
      )}
    </Box>
  );
}
